import React, { Fragment } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

// Utils

import { currencyTools } from '@utils/tools/currencyTools';
import { filterTools } from '@utils/tools/filterTools';
import { internationalizeList } from '@utils/miscellaneous/internationalizeList';
import { toUTCAndFormat } from '@utils/dates/date.utils';

// Constants

import { INCOME_TYPES } from '@constants/ssap/hhmTypes/incomeAndExpenceTypes';
import { DATE_FORMAT } from '@utils/dates/dateMethods';
import { isJobIncomeRowMissingEmployerName } from '@utils/ssap/incomeEmployerNameUtils';

const SourceSummary = props => {

    // TODO Waiver for <NULL> as value for income/deduction type is added temporarily
    // TODO until we find the reason why we are getting it in production
    // TODO it is regarding getSourceName

    const {
        buttonContainer: ButtonContainer,
        sources,
        member,
        dropdownSourcesList,
        dropdownFrequencyList,
        index = undefined,
        summaryType
    } = props;

    const intlApi = useIntl();
    
    
    const getThs = () => {
        const extractTestId = (id) =>
            id === 'common.non_internationalized_text' ? 'actions' : id.split('.').pop();

        const headers = [
            {
                id: 'common.source_type',
                replacements: {
                    sourceType: <FormattedMessage id={summaryType}/>
                },
                customClassName: 'grid-col-2'
            },
            {
                id: 'common.amount',
                customClassName: 'grid-col-2',
                replacements: {}
            },
            {
                id: 'common.frequency',
                customClassName: 'grid-col-2',
                replacements: {}
            },
            {
                id: 'common.start_date',
                customClassName: 'grid-col-2',
                replacements: {}
            },
            {
                id: 'common.end_date',
                customClassName: 'grid-col-2',
                replacements: {}
            },
            {
                id: 'common.non_internationalized_text',
                replacements: {
                    text: <FormattedMessage id = 'common.actions' />,
                },
                customClassName: 'usa-sr-only grid-col-2'
            }
        ].map(header => ({
            ...header,
            dataTestid: `hhm${member}-${extractTestId(header.id)}-header`
        }));
        
        return internationalizeList(headers, 'th', { scope: 'col' });
    };

    const getSources = () => {
        return sources
            .reduce(
                (refinedSources, source) => {
                    return filterTools.removeDummyType(source)
                        ? [ ...refinedSources, source ]
                        : [ ...refinedSources, INCOME_TYPES.DUMMY ];
                },
                []
            )
            .map(
                (source, idx) => {
                    if (source === INCOME_TYPES.DUMMY) {
                        return null;
                    }

                    const getSourceName = () => {
                        const sourceOption = dropdownSourcesList.find(is => is.value === source.type);
                        return sourceOption ? sourceOption.text : 'Unknown source';
                    };

                    const key = `${ source.type }_${ idx }`;
                    const sourceFrequency = dropdownFrequencyList.find(fr => fr.value === source.frequency).text;
                    const sourceFormattedAmount = currencyTools.getMoneyFormat(source.amount, true);
                    const sourceResolvedIndex = index ? index : idx;
                    const NOT_APPLICABLE_TEXT_ID = 'common.not_applicable';
                    const processDate = date => {
                        return date
                            ? toUTCAndFormat(date, DATE_FORMAT)
                            : <FormattedMessage id={NOT_APPLICABLE_TEXT_ID} />;
                    };
                    const sourceStartDate = processDate(source.incomeStartDate);
                    const sourceEndDate = processDate(source.incomeEndDate);

                    const rowHasMissingField = isJobIncomeRowMissingEmployerName(source);
                    const trClassName = rowHasMissingField
                        ? 'grid-row gi-table__row--missing-field'
                        : 'grid-row';

                    return (
                        <tr
                            className={trClassName}
                            aria-invalid={rowHasMissingField}
                            key = { key }
                        >
                            <td className='grid-col-2' data-testid={`hhm${member}-${(source.type).toLowerCase()}`}>{ getSourceName() }</td>
                            <td className='grid-col-2' data-testid={`hhm${member}-${(source.type).toLowerCase()}-amount`}>{ sourceFormattedAmount }</td>
                            <td className='grid-col-2' data-testid={`hhm${member}-${(source.type).toLowerCase()}-frequency`}>{ sourceFrequency }</td>
                            <td className='grid-col-2'data-testid={`hhm${member}-${(source.type).toLowerCase()}-startDate`}>{ sourceStartDate }</td>
                            <td className='grid-col-2'data-testid={`hhm${member}-${(source.type).toLowerCase()}-endDate`}>{ sourceEndDate }</td>
                            <td className='grid-col-2 action-section'>
                                <ButtonContainer
                                    type = 'editButton_secondary_with_icon'
                                    args = { [ sourceResolvedIndex ] }
                                    isDisabled={false}
                                />
                                <ButtonContainer
                                    type='removeButton_with_icon'
                                    args={[member, sourceResolvedIndex]}
                                    isDisabled={false}
                                />
                            </td>
                        </tr>
                    );
                }
            );
    };

    return (
        <Fragment>
            <div className = 'gi-table__wrapper gi-table__responsive' >
                <table
                    className = 'usa-table usa-table--borderless gi-table gi-table__borderless'
                    aria-label = { intlApi.formatMessage({ id: summaryType }) }
                >
                    <thead>
                        <tr className='grid-row'>
                            { getThs() }
                        </tr>
                    </thead>
                    <tbody>
                        { getSources() }
                    </tbody>
                </table>
            </div>
        </Fragment>
    );
};

export default SourceSummary;
